var _excluded = ["status", "className"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React from 'react';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconExclamationTriangleError, IconExclamationTriangleWarning, IconInfoCircle, IconSuccessCircle, useLocale } from '../../pp2';
import { forwardRef, omitThemingProps, StylesProvider, tqc, useMultiStyleConfig, useStyles } from '../../system';
import { createContext, cx } from '../../utils';
var FAIcon = tqc(FontAwesomeIcon);
var STATUSES = {
  info: {
    icon: faInfoCircle,
    colorScheme: 'blue'
  },
  warning: {
    icon: faExclamationTriangle,
    colorScheme: 'yellow'
  },
  success: {
    icon: faCheckCircle,
    colorScheme: 'green'
  },
  error: {
    icon: faExclamationCircle,
    colorScheme: 'red'
  }
};
var WALES_ICONS = {
  info: IconInfoCircle,
  warning: IconExclamationTriangleWarning,
  success: IconSuccessCircle,
  error: IconExclamationTriangleError
};
var [AlertProvider, useAlertContext] = createContext({
  name: 'AlertContext',
  errorMessage: 'useAlertContext: `context` is undefined. Seems you forgot to wrap alert components in `<Alert />`'
});
/**
 * Alert is used to communicate the state or status of a
 * page, feature or action
 */
export var Alert = forwardRef((props, ref) => {
  var _props$colorScheme;
  var _omitThemingProps = omitThemingProps(props),
    {
      status = 'info',
      className
    } = _omitThemingProps,
    rest = _objectWithoutProperties(_omitThemingProps, _excluded);
  var colorScheme = (_props$colorScheme = props.colorScheme) !== null && _props$colorScheme !== void 0 ? _props$colorScheme : STATUSES[status].colorScheme;
  var styles = useMultiStyleConfig('Alert', _objectSpread(_objectSpread({}, props), {}, {
    colorScheme
  }));
  var alertStyles = _objectSpread({
    width: '100%',
    display: 'flex',
    position: 'relative',
    overflow: 'hidden'
  }, styles.container);
  return /*#__PURE__*/React.createElement(AlertProvider, {
    value: {
      status
    }
  }, /*#__PURE__*/React.createElement(StylesProvider, {
    value: styles
  }, /*#__PURE__*/React.createElement(tqc.div, _extends({
    role: "alert",
    ref: ref
  }, rest, {
    className: cx('tqc-alert', className),
    __css: alertStyles
  }))));
});
export var AlertTitle = forwardRef((props, ref) => {
  var {
    className
  } = props;
  var styles = useStyles();
  return /*#__PURE__*/React.createElement(tqc.h2, _extends({
    ref: ref
  }, props, {
    className: cx('tqc-alert__title', className),
    __css: styles.title
  }));
});
AlertTitle.displayName = 'AlertTitle';
export var AlertDescription = forwardRef((props, ref) => {
  var {
    className
  } = props;
  var styles = useStyles();
  var descriptionStyles = _objectSpread({
    display: 'inline'
  }, styles.description);
  return /*#__PURE__*/React.createElement(tqc.p, _extends({
    ref: ref
  }, props, {
    className: cx('tqc-alert__desc', className),
    __css: descriptionStyles
  }));
});
AlertDescription.displayName = 'AlertDescription';
export var AlertIcon = props => {
  var {
    status
  } = useAlertContext();
  var {
    icon: iconForStatus
  } = STATUSES[status];
  var {
    icon: iconProp,
    className
  } = props;
  var styles = useStyles();
  var locale = useLocale();
  var isWales = locale === 'wales';
  var WalesIcon = WALES_ICONS[status];
  return /*#__PURE__*/React.createElement(tqc.span, _extends({
    display: "inherit"
  }, props, {
    className: cx('tqc-alert__icon', className),
    __css: styles.icon
  }), !isWales ? /*#__PURE__*/React.createElement(FAIcon, {
    icon: iconProp || iconForStatus,
    sx: {
      width: '100% !important',
      height: '100%'
    }
  }) : /*#__PURE__*/React.createElement(WalesIcon, {
    width: "1.25rem",
    height: "1.25rem"
  }));
};
AlertIcon.displayName = 'AlertIcon';